<script setup lang="ts">
  import type { Breadcrumb, Section, Pagination } from '@app/types';
  import type { PropType } from 'vue';

  type Module = {
    title: string;
    descriptionShort: string;
  }

  type Category = {
    title: string;
  }

  defineProps({
    breadcrumbs: { type: Array as PropType<Breadcrumb[]>, required: false, default: [] },
    parent: { type: Object, required: false },
    category: { type: Object, required: false },
    sections: { type: Array as PropType<Section[]>, required: false, default: [] },
    module: { type: Object as PropType<Module>, required: true },
    categories: { type: Array as PropType<Category[]>, required: true },
    posts: { type: Object as PropType<Pagination>, required: true },
  });
</script>
<template>
  <main id="main" class="container blog">
    <SfBreadcrumbs :breadcrumbs />
    <SectionRenderer :sections placement="before" />
    <section class="blog__wrapper">
      <header class="blog__header">
        <SfHeading class="blog__heading" large type="h1">
          {{ category?.title || module.title }}
        </SfHeading>
        <div v-if="category?.descriptionShort || module.descriptionShort"
          class="blog__description" v-html="category?.descriptionShort || module.descriptionShort" />
      </header>
      <div class="blog__content">
        <div class="blog__articles">
          <div v-if="posts.data.length > 0" class="blog__articles-content">
            <SiteArticle v-for="post in posts.data" :post header="h2" />
          </div>
          <SfDataPlaceholder v-else>
            {{ $t('SHOP.BLOG.MESSAGE.NO_ENTRIES') }}
          </SfDataPlaceholder>
          <SfPagination :pagination="posts" />
        </div>
        <FiltersBlog :categories :category :parent />
      </div>
    </section>
    <SectionRenderer :sections placement="after" />
  </main>

  <RouterView />
</template>
<style lang="scss" scoped>
  .blog {
    &__articles-content,
    &__content {
      display: grid;
    }

    &__wrapper {
      display: flex;
      flex-direction: column;
      gap: 2rem;
    }

    &__content {
      grid-template-columns: 3fr 1fr;
      gap: 2rem;
      align-items: start;
    }

    &__articles,
    &__header {
      display: flex;
      flex-direction: column;
    }

    &__description {
      font-size: 0.875rem;
    }

    &__articles {
      gap: 2rem;
      padding: 2rem;
      background: var(--section-background);
      border-inline: var(--section-border-inline);
      border-block: var(--section-border-block);
      border-radius: var(--section-border-radius);
    }

    &__header {
      gap: 0.25rem;
    }

    &__articles-content {
      grid-template-columns: repeat(3, 1fr);
      gap: 1.5rem 1rem;
    }

    @media(max-width: 992px) {

      &__header {
        padding: 0 0.625rem
      }

      &__content {
        display: flex;
        flex-direction: column-reverse;
        gap: 1rem;
      }

      &__articles-content {
        gap: 0.75rem;
      }

      &__articles {
        padding: 1rem 0.625rem;
        gap: 1rem;
      }

      &__wrapper {
        grid-template-columns:1fr;
        gap: 1rem;
        margin: 1rem 0;
      }
    }

    @media(max-width: 768px) {

      &__articles-content {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media(max-width: 576px) {

      &__articles-content {
        grid-template-columns: 1fr;
      }
    }
  }
</style>
